/* eslint-disable react-refresh/only-export-components -- statusBadgeVariants is a public compatibility styling contract. */
import * as React from "react";
import { cva } from "class-variance-authority";
import { cn } from "../lib/utils";
import { useTranslation } from "react-i18next";
import { getStatusBadgeTone } from "./status-badge-tone.js";

// Color-named entries remain compatibility aliases. New code uses semantic tones.
const STATUS_TONE_CLASSES = {
  success: "border-success/35 bg-success/10 text-[var(--success-tint-foreground)]",
  warning: "border-warning/40 bg-warning/15 text-[var(--warning-tint-foreground)]",
  destructive: "border-destructive/35 bg-destructive/10 text-[var(--destructive-tint-foreground)]",
  info: "border-info/35 bg-info/10 text-[var(--info-tint-foreground)]",
  blue: "border-info/35 bg-info/10 text-[var(--info-tint-foreground)]",
  neutral: "border-border bg-muted/60 text-muted-foreground",
  orange: "border-warning/40 bg-warning/15 text-[var(--warning-tint-foreground)]",
  pink: "border-destructive/35 bg-destructive/10 text-[var(--destructive-tint-foreground)]",
  purple: "border-primary/25 bg-primary/10 text-foreground",
  cyan: "border-info/35 bg-info/10 text-[var(--info-tint-foreground)]",
  indigo: "border-primary/25 bg-primary/10 text-foreground",
  emerald: "border-success/35 bg-success/10 text-[var(--success-tint-foreground)]",
  rose: "border-destructive/35 bg-destructive/10 text-[var(--destructive-tint-foreground)]",
  amber: "border-warning/40 bg-warning/15 text-[var(--warning-tint-foreground)]",
  lime: "border-success/35 bg-success/10 text-[var(--success-tint-foreground)]",
  teal: "border-success/35 bg-success/10 text-[var(--success-tint-foreground)]",
  sky: "border-info/35 bg-info/10 text-[var(--info-tint-foreground)]",
  fuchsia: "border-primary/25 bg-primary/10 text-foreground",
  violet: "border-primary/25 bg-primary/10 text-foreground",
  zinc: "border-border bg-muted/60 text-muted-foreground",
};

const statusBadgeVariants = cva(
  "inline-flex items-center rounded-[var(--ds-radius-pill)] border px-[var(--ds-space-2-5)] py-[var(--ds-space-half)] text-xs font-semibold tracking-[var(--ds-type-control-tracking)]",
  { variants: { variant: STATUS_TONE_CLASSES }, defaultVariants: { variant: "neutral" } }
);

const StatusBadge = React.forwardRef(({ className, variant, status, children, ...props }, ref) => {
  const { t } = useTranslation();
  const displayContent = children ?? (status ? t([`status.${status}`, status]) : null);
  return (
    <div ref={ref} data-slot="status-badge" className={cn(statusBadgeVariants({ variant: getStatusBadgeTone(status, variant) }), className)} {...props}>
      {displayContent}
    </div>
  );
});
StatusBadge.displayName = "StatusBadge";

export { StatusBadge, statusBadgeVariants };
